import React from 'react';
import { Redirect, Route, Switch } from '@react-router';
import { FormikProps } from 'formik';
import { ErrorSplash } from '@shared/components';
import { useContactCollectorIframeController, Contacts } from './ContactCollectorIframe.controller';
import { RoutePath, RoutesProvider, useRouterHelper, useTranslation } from '@shared/core';
import { TelemetryProvider } from '../guest/ContactCollectorGuest.telemetry';
import { Form, Address } from '../guest/routes';
import { JoyLogoLoader } from '@shared/components/JoyLogoLoader';

interface ContactCollectorIframeProps
  extends Readonly<{
    firebaseEventId: string;
    contactId?: string;
  }> {}

export const contactCollectorIframeRoutePaths = {
  form: {
    path: 'form',
    goToPath: () => 'form',
  },
  address: {
    path: 'address',
    goToPath: () => 'address',
  },
};

const RoutesContactCollectorIframe: React.FC<{
  formik: FormikProps<Contacts>;
  isMutationLoading: boolean;
  hasMutationError: boolean;
  e164PhoneNumber: { [key: number]: string };
  setE164PhoneNumber: React.Dispatch<React.SetStateAction<{ [key: number]: string }>>;
  isUpdateMode: boolean;
}> = ({ formik, isMutationLoading, hasMutationError, e164PhoneNumber, setE164PhoneNumber, isUpdateMode }) => {
  const routeHelpers = useRouterHelper();

  const routes: Array<RoutePath<{}>> = [
    {
      path: routeHelpers.buildPath(contactCollectorIframeRoutePaths.form.path),
      component: () => (
        <Form
          formik={formik}
          isCollectHouseholdEnabled={!isUpdateMode}
          isIframeVersion
          routeHelpers={routeHelpers}
          isMutationLoading={isMutationLoading}
          hasMutationError={hasMutationError}
          e164PhoneNumber={e164PhoneNumber}
          setE164PhoneNumber={setE164PhoneNumber}
          isMailingAddressOpenedByDefault={isUpdateMode}
          hideTermsAndConditions
          formatIntlTelInputOnInit={!isUpdateMode}
        />
      ),
    },
    {
      path: routeHelpers.buildPath(contactCollectorIframeRoutePaths.address.path),
      component: () => (
        <Address
          formik={formik}
          isIframeVersion
          isMutationLoading={isMutationLoading}
          hasMutationError={hasMutationError}
        />
      ),
    },
  ];

  return (
    <Switch>
      {routes.map(route => (
        <Route
          key={route.path}
          path={route.path}
          render={route.component}
          exact
        />
      ))}
      <Redirect to={routes[0].path} />
    </Switch>
  );
};

const ContactCollectorIframe: React.FC<ContactCollectorIframeProps> = ({ firebaseEventId, contactId }) => {
  const { tq } = useTranslation('contactCollector');
  const isUpdateMode = !!contactId;
  const { showErrorPage, isLoading, formik, isMutationLoading, hasMutationError, telemetryData, e164PhoneNumber, setE164PhoneNumber } = useContactCollectorIframeController(
    firebaseEventId,
    contactId,
  );

  if (isLoading) return <JoyLogoLoader loaderKey="contact-collector-iframe" />;
  if (showErrorPage) return <ErrorSplash customError={{ title: tq`Hmm… we’re having some trouble.`, subtitle: tq`This page may have changed or is temporarily unavailable.` }} />;

  return (
    <TelemetryProvider context={telemetryData}>
      <RoutesProvider appUrl={isUpdateMode ? 'contact-iframe/:firebaseEventId/update/:contactId' : 'contact-iframe/:firebaseEventId'}>
        <RoutesContactCollectorIframe
          formik={formik}
          isMutationLoading={isMutationLoading}
          hasMutationError={hasMutationError}
          e164PhoneNumber={e164PhoneNumber}
          setE164PhoneNumber={setE164PhoneNumber}
          isUpdateMode={isUpdateMode}
        />
      </RoutesProvider>
    </TelemetryProvider>
  );
};

export default ContactCollectorIframe;
